import { useEffect, useState, useCallback } from "react";
import { X, Sparkles, ArrowRight, Loader2, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { EBOOK, openEbook, submitEbookLead, track } from "@/lib/ebook";

const DISMISS_KEY = "cc_exit_intent_dismissed_v1";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { pathname } = useLocation();

  const show = useCallback(() => {
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_) {}
    setOpen(true);
    track("exit_intent_view", pathname);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
  }, [pathname]);

  useEffect(() => {
    // Suppress on admin/checkout pages
    if (pathname.startsWith("/admin") || pathname.startsWith("/checkout")) return;
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_) {}

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 10_000); // arm after 10s on page

    const onMouseLeave = (e) => {
      if (!armed) return;
      // Triggered when cursor moves above the viewport (close-tab gesture)
      if (e.clientY <= 0 && (e.relatedTarget == null || e.relatedTarget?.nodeName === "HTML")) {
        show();
      }
    };

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [pathname, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await submitEbookLead({ email, source: "exit-intent" });
      toast.success("Sent. Your guide is opening now.");
      openEbook("exit-intent");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign up. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent"
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="relative bg-[#111111] border border-[#D4AF37]/40 max-w-lg w-full shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] animate-in zoom-in-95 duration-300">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden />
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-white/60 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-8 md:p-10">
          <div className="flex items-center gap-2 overline text-[#D4AF37]">
            <Sparkles className="w-3.5 h-3.5" />
            Before You Go
          </div>
          <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mt-4 text-white">
            One read could change<br />
            your <span className="italic text-[#D4AF37]">whole canvas.</span>
          </h3>
          <p className="text-white/65 leading-relaxed mt-4 font-light text-sm md:text-base">
            Drop your email and we&apos;ll send the Crowned Perfect Photo Guide — the difference between a good photo and a great canvas. No spam. Just craft.
          </p>
          <form onSubmit={handleSubmit} data-testid="exit-intent-form" className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input
              required
              type="email"
              data-testid="exit-intent-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-black border-white/15 h-12 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] flex-1"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="exit-intent-submit"
              className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 h-12 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send It<ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <button
            type="button"
            onClick={() => { openEbook("exit-intent-skip"); setOpen(false); }}
            data-testid="exit-intent-skip"
            className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/50 hover:text-[#D4AF37] transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Skip the email — just read it
          </button>
        </div>
      </div>
    </div>
  );
}
